import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LETTERHEAD_IMG } from "./letterheadImg";

const CONTENT_TOP = 130; // area kop surat (header hijau/oranye) berakhir ± 105pt
const BOTTOM_MARGIN = 56;
const MARGIN_X = 48;

function formatIDR(v) {
    const n = Number(v) || 0;
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
    }).format(n);
}

// Harga efektif paket: pakai Harga Jual Paket, jika 0 pakai Harga Jual RS
function effPrice(i) {
    const pp = Number(i.price_paket) || 0;
    return pp > 0 ? pp : Number(i.price) || 0;
}

// Diskon per item (persen atau nominal), dibatasi 0..harga efektif
function itemDiscount(i) {
    const base = effPrice(i);
    const v = Number(i.discount_value) || 0;
    const d = i.discount_type === "amount" ? v : (base * v) / 100;
    return Math.min(Math.max(d, 0), base);
}

// Harga net item = harga efektif - diskon item
function itemNet(i) {
    return effPrice(i) - itemDiscount(i);
}

function todayID() {
    return new Date().toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

function drawLetterhead(doc) {
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    try {
        doc.addImage(LETTERHEAD_IMG, "JPEG", 0, 0, w, h);
    } catch {
        // abaikan bila gambar gagal dimuat
    }
}

function makeWillDrawPage(doc, stamped) {
    return () => {
        const p = doc.internal.getCurrentPageInfo().pageNumber;
        if (!stamped.has(p)) {
            drawLetterhead(doc);
            stamped.add(p);
        }
    };
}

function ensureSpace(doc, y, needed, stamped) {
    if (y + needed > doc.internal.pageSize.getHeight() - BOTTOM_MARGIN) {
        doc.addPage();
        drawLetterhead(doc);
        stamped.add(doc.internal.getNumberOfPages());
        return CONTENT_TOP;
    }
    return y;
}

const KIND_SECTIONS = [
    { key: "lab", label: "Pemeriksaan Laboratorium" },
    { key: "radiology", label: "Pemeriksaan Radiologi / Penunjang" },
    { key: "jasa", label: "Jasa Tindakan" },
    { key: "obat", label: "Obat & BMHP" },
    { key: "fasilitas", label: "Fasilitas" },
];

// ============ PDF PROPOSAL (untuk pelanggan) ============
export function generatePackagePDF(pkg, settings, options = {}) {
    // options.detailed: true (default) = daftar nama pemeriksaan; false = ringkas (nama paket + total)
    // options.showSubtotal / options.showDiscount: tampilkan baris Subtotal / Diskon (default true)
    const detailed = options.detailed !== false;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const stamped = new Set([1]);
    drawLetterhead(doc);

    // ---- Judul ----
    doc.setTextColor(26, 54, 45);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("PROPOSAL PAKET PEMERIKSAAN", pageW / 2, CONTENT_TOP + 16, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(74, 94, 87);
    doc.text(`Tanggal: ${todayID()}`, pageW - MARGIN_X, CONTENT_TOP + 36, { align: "right" });

    // ---- Info Paket ----
    let y = CONTENT_TOP + 58;
    doc.setTextColor(26, 54, 45);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(pkg.name || "Paket Pemeriksaan", MARGIN_X, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(74, 94, 87);
    if (pkg.patient_target) {
        doc.text(`Target: ${pkg.patient_target}`, MARGIN_X, y);
        y += 14;
    }
    if (pkg.description) {
        const desc = doc.splitTextToSize(pkg.description, pageW - MARGIN_X * 2);
        doc.text(desc, MARGIN_X, y);
        y += desc.length * 12;
    }
    y += 10;

    // ---- Tabel item ----
    if (detailed) {
        for (const sec of KIND_SECTIONS) {
            const rows = (pkg.items || []).filter((i) => i.kind === sec.key);
            if (!rows.length) continue;
            autoTable(doc, {
                startY: y,
                head: [["Kode", sec.label, "Harga"]],
                body: rows.map((i) => [i.code, i.name, formatIDR(itemNet(i))]),
                styles: { fontSize: 9, cellPadding: 5, textColor: [26, 54, 45] },
                headStyles: {
                    fillColor: [36, 91, 75],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                },
                alternateRowStyles: { fillColor: [247, 249, 246] },
                margin: { left: MARGIN_X, right: MARGIN_X, top: CONTENT_TOP, bottom: BOTTOM_MARGIN },
                columnStyles: { 0: { cellWidth: 70 }, 2: { halign: "right", cellWidth: 90 } },
                willDrawPage: makeWillDrawPage(doc, stamped),
            });
            y = doc.lastAutoTable.finalY + 14;
        }
    } else {
        // Ringkas: satu baris nama paket + total, tanpa nama pemeriksaan
        autoTable(doc, {
            startY: y,
            head: [["Paket", "Total"]],
            body: [[pkg.name || "Paket Pemeriksaan", formatIDR(pkg.total_price)]],
            styles: { fontSize: 9, cellPadding: 5, textColor: [26, 54, 45] },
            headStyles: {
                fillColor: [36, 91, 75],
                textColor: [255, 255, 255],
                fontStyle: "bold",
            },
            alternateRowStyles: { fillColor: [247, 249, 246] },
            margin: { left: MARGIN_X, right: MARGIN_X, top: CONTENT_TOP, bottom: BOTTOM_MARGIN },
            columnStyles: { 1: { halign: "right", cellWidth: 110 } },
            willDrawPage: makeWillDrawPage(doc, stamped),
        });
        y = doc.lastAutoTable.finalY + 14;
    }

    // ---- Ringkasan (tanpa informasi Margin) ----
    const showSubtotal = options.showSubtotal !== false;
    const showDiscount = options.showDiscount !== false;
    const qty = Number(pkg.qty) || 1;
    const grandTotal =
        pkg.grand_total != null ? Number(pkg.grand_total) : Number(pkg.final_price || 0) * qty;
    const rowH = 18;
    const boxH = 82 + (showSubtotal ? rowH : 0) + (showDiscount ? rowH : 0);
    y = ensureSpace(doc, y, boxH + 20, stamped);
    const summaryY = y;
    doc.setDrawColor(196, 218, 210);
    doc.rect(MARGIN_X, summaryY, pageW - MARGIN_X * 2, boxH);

    let ly = summaryY + 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (showSubtotal) {
        doc.setTextColor(74, 94, 87);
        doc.text("Subtotal", MARGIN_X + 12, ly);
        doc.setTextColor(26, 54, 45);
        doc.text(formatIDR(pkg.total_price), pageW - MARGIN_X - 12, ly, { align: "right" });
        ly += rowH;
    }

    if (showDiscount) {
        const isAmountDisc = pkg.discount_type === "amount";
        const discAmount = isAmountDisc
            ? Number(pkg.discount_amount) || 0
            : Number(pkg.total_price || 0) * (Number(pkg.discount_percent || 0) / 100);
        doc.setTextColor(74, 94, 87);
        doc.text(
            isAmountDisc ? "Diskon" : `Diskon ${pkg.discount_percent || 0}%`,
            MARGIN_X + 12,
            ly
        );
        doc.setTextColor(26, 54, 45);
        doc.text(`- ${formatIDR(discAmount)}`, pageW - MARGIN_X - 12, ly, { align: "right" });
        ly += rowH;
    }

    ly += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(36, 91, 75);
    doc.text("HARGA PAKET FINAL / PASIEN", MARGIN_X + 12, ly);
    doc.setFontSize(13);
    doc.text(formatIDR(pkg.final_price), pageW - MARGIN_X - 12, ly, { align: "right" });
    ly += rowH;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(74, 94, 87);
    doc.text("Qty (Jumlah Pasien)", MARGIN_X + 12, ly);
    doc.setTextColor(26, 54, 45);
    doc.text(`${qty}`, pageW - MARGIN_X - 12, ly, { align: "right" });
    ly += 24;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(36, 91, 75);
    doc.text("GRAND TOTAL", MARGIN_X + 12, ly);
    doc.setFontSize(15);
    doc.text(formatIDR(grandTotal), pageW - MARGIN_X - 12, ly, { align: "right" });

    doc.save(`${(pkg.name || "paket-pemeriksaan").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}

// ============ PDF PENGAJUAN INTERNAL (Manager Keuangan & Direktur) ============
export function generateInternalPDF(pkg, settings) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const stamped = new Set([1]);
    drawLetterhead(doc);

    const items = pkg.items || [];
    const totalUC = items.reduce(
        (s, i) =>
            s +
            (Number(i.unit_cost) || 0) +
            (Number(i.unit_cost_lanjutan) || 0) +
            (Number(i.jasa_medis) || 0),
        0
    );
    const totalRS = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
    const totalPaketGross = items.reduce((s, i) => s + effPrice(i), 0);
    const totalItemDisc = items.reduce((s, i) => s + itemDiscount(i), 0);
    const netSubtotal = totalPaketGross - totalItemDisc;
    const discountPct = Number(pkg.discount_percent) || 0;
    const isAmountDisc = pkg.discount_type === "amount";
    const discountValue = isAmountDisc
        ? Number(pkg.discount_amount) || 0
        : netSubtotal * (discountPct / 100);
    const finalPrice =
        pkg.final_price != null
            ? Number(pkg.final_price)
            : Math.max(netSubtotal - discountValue, 0);
    const qty = Number(pkg.qty) || 1;
    const grandTotal = pkg.grand_total != null ? Number(pkg.grand_total) : finalPrice * qty;

    // ---- Judul ----
    doc.setTextColor(26, 54, 45);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("PENGAJUAN INTERNAL HARGA PAKET PEMERIKSAAN", pageW / 2, CONTENT_TOP + 16, {
        align: "center",
    });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(74, 94, 87);
    doc.text("Dokumen internal — bukan untuk pelanggan", pageW / 2, CONTENT_TOP + 30, {
        align: "center",
    });

    // ---- Kepada / Perihal ----
    let y = CONTENT_TOP + 54;
    doc.setFontSize(10);
    doc.setTextColor(26, 54, 45);
    doc.text(`Kepada Yth.  : Manager Keuangan & Direktur ${settings?.name || ""}`, MARGIN_X, y);
    y += 15;
    doc.text(`Perihal          : Pengajuan Persetujuan Harga Paket "${pkg.name || "-"}"`, MARGIN_X, y);
    y += 15;
    doc.text(`Tanggal        : ${todayID()}`, MARGIN_X, y);
    y += 15;
    if (pkg.patient_target) {
        doc.text(`Target           : ${pkg.patient_target}`, MARGIN_X, y);
        y += 15;
    }
    y += 6;

    // ---- Tabel detail per jenis ----
    for (const sec of KIND_SECTIONS) {
        const rows = items.filter((i) => i.kind === sec.key);
        if (!rows.length) continue;
        autoTable(doc, {
            startY: y,
            head: [[sec.label, "Total Unit Cost", "Harga RS", "Harga Paket", "Diskon Item", "Harga Net", "Margin Awal"]],
            body: rows.map((i) => {
                const uc =
                    (Number(i.unit_cost) || 0) +
                    (Number(i.unit_cost_lanjutan) || 0) +
                    (Number(i.jasa_medis) || 0);
                const disc = itemDiscount(i);
                const net = itemNet(i);
                const marginAwal = net > 0 ? `${(((net - uc) / net) * 100).toFixed(1)}%` : "-";
                return [
                    `${i.code}  ${i.name}`,
                    formatIDR(uc),
                    formatIDR(i.price),
                    formatIDR(effPrice(i)),
                    disc > 0 ? `- ${formatIDR(disc)}` : "-",
                    formatIDR(net),
                    marginAwal,
                ];
            }),
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 54, 45] },
            headStyles: {
                fillColor: [36, 91, 75],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 7.5,
            },
            alternateRowStyles: { fillColor: [247, 249, 246] },
            margin: { left: MARGIN_X, right: MARGIN_X, top: CONTENT_TOP, bottom: BOTTOM_MARGIN },
            columnStyles: {
                1: { halign: "right", cellWidth: 58 },
                2: { halign: "right", cellWidth: 58 },
                3: { halign: "right", cellWidth: 60 },
                4: { halign: "right", cellWidth: 58 },
                5: { halign: "right", cellWidth: 60 },
                6: { halign: "right", cellWidth: 42 },
            },
            willDrawPage: makeWillDrawPage(doc, stamped),
        });
        y = doc.lastAutoTable.finalY + 12;
    }

    // ---- Ringkasan keuangan (internal) ----
    const surplus = finalPrice - totalUC;
    const surplusPct = totalUC > 0 ? (surplus / totalUC) * 100 : 0;
    const rows = [
        ["Total Unit Cost (Dasar + Lanjutan + Jasa Medis)", formatIDR(totalUC)],
        ["Total Harga Jual RS", formatIDR(totalRS)],
        ["Subtotal Harga Jual Paket", formatIDR(totalPaketGross)],
    ];
    if (totalItemDisc > 0) {
        rows.push(["Total Diskon per Item", `- ${formatIDR(totalItemDisc)}`]);
        rows.push(["Subtotal setelah Diskon Item", formatIDR(netSubtotal)]);
    }
    rows.push([
        isAmountDisc ? "Diskon" : `Diskon ${discountPct}%`,
        `- ${formatIDR(discountValue)}`,
    ]);
    const finalRowIdx = rows.length;
    rows.push(["Harga Paket Final / Pasien", formatIDR(finalPrice)]);
    rows.push([
        "Estimasi Surplus / Pasien (Final - Total Unit Cost)",
        `${formatIDR(surplus)} (${surplusPct.toFixed(1)}% dari Total Unit Cost)`,
    ]);
    rows.push(["Qty (Jumlah Pasien)", `${qty}`]);
    rows.push(["GRAND TOTAL", formatIDR(grandTotal)]);
    autoTable(doc, {
        startY: y,
        head: [["Ringkasan Keuangan", "Nilai"]],
        body: rows,
        styles: { fontSize: 9, cellPadding: 5, textColor: [26, 54, 45] },
        headStyles: { fillColor: [36, 91, 75], textColor: [255, 255, 255], fontStyle: "bold" },
        margin: { left: MARGIN_X, right: MARGIN_X, top: CONTENT_TOP, bottom: BOTTOM_MARGIN },
        columnStyles: { 1: { halign: "right", cellWidth: 190 } },
        didParseCell: (data) => {
            if (
                data.section === "body" &&
                (data.row.index === finalRowIdx || data.row.index === rows.length - 1)
            ) {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.textColor = [36, 91, 75];
            }
        },
        willDrawPage: makeWillDrawPage(doc, stamped),
    });
    y = doc.lastAutoTable.finalY + 18;

    // ---- Kalimat penutup ----
    y = ensureSpace(doc, y, 40, stamped);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(26, 54, 45);
    const closing = doc.splitTextToSize(
        "Demikian pengajuan harga paket pemeriksaan ini kami sampaikan. Mohon persetujuan Bapak/Ibu. Atas perhatian dan kerja samanya kami ucapkan terima kasih.",
        pageW - MARGIN_X * 2
    );
    doc.text(closing, MARGIN_X, y);
    y += closing.length * 13 + 14;

    // ---- Area tanda tangan ----
    y = ensureSpace(doc, y, 170, stamped);
    doc.setFontSize(10);
    doc.text(`${settings?.city || "Tangerang"}, ${todayID()}`, pageW - MARGIN_X, y, {
        align: "right",
    });
    y += 24;

    const colW = (pageW - MARGIN_X * 2) / 2;
    const leftX = MARGIN_X + colW / 2;
    const rightX = MARGIN_X + colW + colW / 2;
    const managerName = settings?.sign_manager_name?.trim() || "(.............................)";
    const directorName = settings?.sign_director_name?.trim() || "(.............................)";

    doc.setFont("helvetica", "normal");
    doc.text("Menyetujui,", leftX, y, { align: "center" });
    doc.text("Mengetahui & Menyetujui,", rightX, y, { align: "center" });
    y += 14;
    doc.setFont("helvetica", "bold");
    doc.text("Manager Keuangan", leftX, y, { align: "center" });
    doc.text("Direktur", rightX, y, { align: "center" });

    const signY = y + 78; // ruang tanda tangan
    doc.setFont("helvetica", "bold");
    doc.text(managerName, leftX, signY, { align: "center" });
    doc.text(directorName, rightX, signY, { align: "center" });
    // garis nama
    const lineHalf = 80;
    doc.setDrawColor(26, 54, 45);
    doc.setLineWidth(0.8);
    doc.line(leftX - lineHalf, signY + 4, leftX + lineHalf, signY + 4);
    doc.line(rightX - lineHalf, signY + 4, rightX + lineHalf, signY + 4);

    doc.save(
        `pengajuan-internal-${(pkg.name || "paket").replace(/\s+/g, "-").toLowerCase()}.pdf`
    );
}

export function buildWhatsAppText(pkg, settings) {
    const lines = [];
    lines.push(`*${settings?.name || "Rumah Sakit"}*`);
    if (settings?.address) lines.push(settings.address);
    if (settings?.phone) lines.push(`Telp: ${settings.phone}`);
    lines.push("");
    lines.push(`*Proposal Paket Pemeriksaan: ${pkg.name}*`);
    if (pkg.patient_target) lines.push(`Target: ${pkg.patient_target}`);
    if (pkg.description) lines.push(pkg.description);
    lines.push("");
    const sections = [
        ["lab", "*Pemeriksaan Laboratorium:*"],
        ["radiology", "*Pemeriksaan Radiologi / Penunjang:*"],
        ["jasa", "*Jasa Tindakan:*"],
        ["obat", "*Obat & BMHP:*"],
        ["fasilitas", "*Fasilitas:*"],
    ];
    for (const [key, label] of sections) {
        const list = (pkg.items || []).filter((i) => i.kind === key);
        if (!list.length) continue;
        lines.push(label);
        list.forEach((i) => lines.push(`• ${i.name}`));
        lines.push("");
    }
    const qty = Number(pkg.qty) || 1;
    const grandTotal =
        pkg.grand_total != null ? Number(pkg.grand_total) : Number(pkg.final_price || 0) * qty;
    lines.push(`*Harga Paket / Pasien: ${formatIDR(pkg.final_price)}*`);
    if (qty > 1) {
        lines.push(`Qty: ${qty} pasien`);
        lines.push(`*Grand Total: ${formatIDR(grandTotal)}*`);
    }
    lines.push("");
    lines.push("Untuk informasi lebih lanjut, silakan hubungi kami. Terima kasih.");
    return lines.join("\n");
}
